import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { 
  SidebarComponent, 
  SidebarMenuItem, 
  SidebarCallToAction, 
  SidebarFooterItem 
} from '../../shared/components/sidebar/sidebar.component';
import { AuthApiService } from '@angular-workspace/services';

interface Organisation {
  status?: 'pending' | 'granted' | 'rejected';
  request_type?: string;
  orgName?: string;
  entityType?: string;
  entitySector?: string;
  websiteUrl?: string;
  address?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-consumer-sidebar',
  standalone: true,
  imports: [CommonModule, SidebarComponent],
  templateUrl: './consumer-sidebar.component.html',
  styleUrl: './consumer-sidebar.component.scss',
})
export class ConsumerSidebarComponent implements OnInit {
  organisation: Organisation | null = null;
  organisationStatus: 'pending' | 'granted' | null = null;
  organisationName: string | null = null;
  loading = true;

  menuItems: SidebarMenuItem[] = [];

  callToAction: SidebarCallToAction | undefined = {
    title: 'Join an Organisation',
    description:
      'Register or join an Organisation on the platform to begin contributing Datasets, Models, Usecases.',
    linkText: 'Explore Further',
    linkRoute: '/consumer/join-organisation',
    iconUrl: '/icons/building.svg',
  };

  constructor(
    private authApiService: AuthApiService,
    private route: ActivatedRoute
  ) {}

  async ngOnInit(): Promise<void> {
    // After withdraw, we navigate with ?withdrawn=1 – show "Join an Organisation" immediately
    if (this.route.snapshot.queryParamMap.get('withdrawn') === '1') {
      this.clearOrganisationState();
      return;
    }
    await this.loadUserOrganisation();
    this.updateMenuItems();
    this.updateCallToAction();
  }

  /** Clear organisation state so sidebar shows "Join an Organisation" (e.g. after withdraw). */
  clearOrganisationState(): void {
    this.organisation = null;
    this.organisationStatus = null;
    this.organisationName = null;
    this.loading = false;
    this.updateMenuItems();
    this.updateCallToAction();
  }

  async loadUserOrganisation(): Promise<void> {
    try {
      this.loading = true;
      const userInfo = await this.authApiService.getUserInfo();
      
      // Extract organisation from user API response
      // The organisation might be in result.organisation, result.org, or directly in result
      const result = userInfo['result'] || userInfo;
      const org = result['organisation'] || result['organization'] || result['org'] || null;
      
      // Extract organisation name from result (it's at root level, not in org object)
      this.organisationName = result['organisationName'] || result['organizationName'] || null;
      
      if (org && org.status) {
        this.organisation = org;
        // Set status only if it's 'pending' or 'granted'
        this.organisationStatus = org.status === 'pending' || org.status === 'granted' ? org.status : null;
        
        // Debug: Log organisation data to verify it's being extracted correctly
        console.log('Organisation data:', {
          status: org.status,
          request_type: org.request_type || org['request_type'] || org['requestType'],
          organisation: this.organisation
        });
      } else {
        this.organisation = null;
        this.organisationStatus = null;
      }
    } catch (error) {
      console.error('Error loading user organisation:', error);
      this.organisation = null;
      this.organisationStatus = null;
      this.organisationName = null;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Refresh sidebar from API, or optimistically clear organisation (e.g. after withdraw).
   * @param clearOrganisation When true, clear org state immediately so "Join an Organisation" shows without waiting for API.
   */
  async refreshOrganisationStatus(clearOrganisation?: boolean): Promise<void> {
    if (clearOrganisation) {
      this.clearOrganisationState();
      return;
    }
    await this.loadUserOrganisation();
    this.updateMenuItems();
    this.updateCallToAction();
  }

  updateMenuItems(): void {
    // Base menu items
    const baseMenuItems: SidebarMenuItem[] = [
      {
        label: 'Dashboard',
        route: '/consumer/home',
        iconUrl: '/icons/dashboard.svg',
      },
      {
        label: 'My Activities',
        route: '/consumer/activities',
        iconUrl: '/icons/activities.svg',
      },
      {
        label: 'My Request',
        route: '/consumer/requests',
        iconUrl: '/icons/files.svg',
      },
      {
        label: 'My Projects',
        route: '/consumer/my-projects',
        iconUrl: '/icons/folder.svg',
      },
      // {
      //   label: 'Delegate',
      //   route: '/consumer/delegate',
      //   icon: '<path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="8.5" cy="7" r="4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M17 11L22 16L17 21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M22 16H12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
      // },
    ];

    // Check if organisation is granted and request_type is organisation_join
    const isOrganisationGranted = 
      this.organisationStatus === 'granted' && 
      this.organisation && 
      (this.organisation.request_type === 'organisation_join' || 
       this.organisation['request_type'] === 'organisation_join' ||
       this.organisation['requestType'] === 'organisation_join') &&
      this.organisationName;

    // Add Organisation menu item if conditions are met
    if (isOrganisationGranted) {
      this.menuItems = [
        ...baseMenuItems,
        {
          label: 'Organisation',
          route: '/consumer/data-provider-entity',
          iconUrl: '/icons/building.svg',
        },
      ];
    } else {
      this.menuItems = baseMenuItems;
    }
  }

  updateCallToAction(): void {
    // Check if there's a pending organisation_join request
    const isPendingOrganisationJoin = 
      this.organisationStatus === 'pending' && 
      this.organisation && 
      (this.organisation.request_type === 'organisation_join' || 
       this.organisation['request_type'] === 'organisation_join' ||
       this.organisation['requestType'] === 'organisation_join');
    
    // Check if there's a pending organisation_create request
    const isPendingOrganisationCreate = 
      this.organisationStatus === 'pending' && 
      this.organisation && 
      (this.organisation.request_type === 'organisation_create' || 
       this.organisation['request_type'] === 'organisation_create' ||
       this.organisation['requestType'] === 'organisation_create');
    
    if (isPendingOrganisationJoin || isPendingOrganisationCreate) {
      // If organisation object exists, show data-provider-entity-form (view-only)
      // If no organisation object, show create-organisation stepper
      const hasOrganisation = this.organisation && Object.keys(this.organisation).length > 0;
      const targetRoute = hasOrganisation 
        ? '/consumer/data-provider-entity' 
        : '/consumer/create-organisation';
      
      // Show "Awaiting Approval" message - make it clickable to navigate to appropriate page
      this.callToAction = {
        title: 'Awaiting Approval',
        description: 'Your request will be approved by the organisation manager. shortly !!',
        linkText: '',
        linkRoute: targetRoute,
        iconUrl: '/icons/timer-black.svg',
      };
    } else if (
      this.organisationStatus === 'granted' && 
      this.organisation && 
      (this.organisation.request_type === 'organisation_join' || 
       this.organisation['request_type'] === 'organisation_join' ||
       this.organisation['requestType'] === 'organisation_join') &&
      this.organisationName
    ) {
      // Organisation is granted - hide callToAction (Organisation will be shown as menu item)
      this.callToAction = undefined;
    } else {
      // Default: Show "Join an Organisation" only if there's no pending or granted organisation
      this.callToAction = {
        title: 'Join an Organisation',
        description:
          'Register or join an Organisation on the platform to begin contributing Datasets, Models, Usecases.',
        linkText: 'Explore Further',
        linkRoute: '/consumer/join-organisation',
        iconUrl: '/icons/building.svg',
      };
    }
  }

  footerItems: SidebarFooterItem[] = [
    {
      label: 'Profile',
      route: '/consumer/profile',
      iconUrl: '/icons/person.svg',
    },
    {
      label: 'Support',
      route: '/consumer/support',
      iconUrl: '/icons/support.svg',
    },
  ];
}

